import { z } from 'zod';
import { ValidationDepth, ValidationDepthSchema } from '../enumerations/ValidationDepth';
import { ArtifactResource } from './artifactResourceTypes';
import { IdSchema, NameSchema } from './baseSchemas';
import { msObject } from './msObject';
import { PersistableResource } from './persistableResourceTypes';
import { UpdatedSettingSchema } from './settings/reactCommonsSchemas';
import { UpdatedSetting } from './settings/types';

export const ActivationMutateObjectSchema = msObject({
    id: IdSchema,
    activateId: IdSchema,
    variantId: IdSchema.nullish(),
});

export const DismissTaskMutateObjectSchema = msObject({
    id: IdSchema,
});

export const CloneMutateObjectSchema = msObject({
    id: IdSchema,
    names: NameSchema.array(),
    projectLineVersionId: IdSchema.optional(),
});

export const CopyActivitiesMutateObjectSchema = msObject({
    sourceProjectVersionId: IdSchema,
    targetProjectVersionId: IdSchema,
    activitiesList: z
        .object({
            activityObjectId: IdSchema,
            insertName: z.string(),
        })
        .array(),
});

export const SystemIncludeGenerationMutateObjectSchema = msObject({
    id: IdSchema,
    name: NameSchema,
    generateSystemConstantDefinitionFile: z.boolean(),
    additionalIncludes: z.string().array(),
});

export const SystemConstantGenerateMutateObjectSchema = msObject({
    id: IdSchema,
    name: NameSchema,
});

export const NewVersionMutateObjectSchema = msObject({
    precedingVersion: IdSchema,
});

export type ActivationMutateObject = z.infer<typeof ActivationMutateObjectSchema>;
export type DismissTaskMutateObject = z.infer<typeof DismissTaskMutateObjectSchema>;
export type CloneMutateObject = z.infer<typeof CloneMutateObjectSchema>;
export type CopyActivitiesMutateObject = z.infer<typeof CopyActivitiesMutateObjectSchema>;
export type SystemIncludeGenerationMutateObject = z.infer<typeof SystemIncludeGenerationMutateObjectSchema>;
export type SystemConstantGenerateMutateObject = z.infer<typeof SystemConstantGenerateMutateObjectSchema>;
export type NewVersionMutateObject = z.infer<typeof NewVersionMutateObjectSchema>;

export const UpdateSettingsByCategoryMutateObjectSchema = msObject({
    id: IdSchema,
    category: z.string(),
    settings: UpdatedSettingSchema.array(),
    validationDepth: ValidationDepthSchema.optional(),
});

export interface UpdateSettingsByCategoryMutateObject {
    id: string | undefined;
    category: string;
    settings: UpdatedSetting[];
    validationDepth?: ValidationDepth;
}

export const UpdateSettingMutateObjectSchema = msObject({
    id: IdSchema,
    setting: UpdatedSettingSchema,
    validationDepth: ValidationDepthSchema.optional(),
});

export interface UpdateSettingMutateObject {
    id: string | undefined;
    setting: UpdatedSetting | undefined;
    validationDepth?: ValidationDepth;
}

export const IdMutateObjectSchema = msObject({
    id: IdSchema,
});

export interface IdMutateObject {
    id: string | undefined;
}

// TODO remove these
export const CloseVersionMutateObjectSchema = IdMutateObjectSchema;

export interface CloseVersionMutateObject extends IdMutateObject {}

export const DeleteMutateObjectSchema = IdMutateObjectSchema;

export interface DeleteMutateObject extends IdMutateObject {}

export const GenericDeleteMutateObjectSchema = DeleteMutateObjectSchema.extend({
    persistableType: z.string(),
});

export interface GenericDeleteMutateObject extends DeleteMutateObject {
    persistableType: string;
}

export const UpdateMutateObjectSchema = msObject({
    resource: z.unknown(),
    id: IdSchema,
    validationDepth: ValidationDepthSchema.optional(),
});

export interface UpdateMutateObject<R extends PersistableResource> {
    resource: R;
    id: string;
    validationDepth?: ValidationDepth;
}

export const BatchAddMutateObjectSchema = msObject({
    payload: z.record(z.string(), z.unknown().array()),
});

export interface BatchAddMutateObject<R extends PersistableResource> {
    payload: Record<string, R[]>;
}

export const BatchAddMutateObjectWithRelationSchema = msObject({
    payload: z.record(z.string(), z.unknown().array()),
    parentRelation: z.string().optional(),
});

export interface BatchAddMutateObjectWithRelation<R extends PersistableResource> {
    payload: Record<string, R[]>;
    parentRelation: string | undefined;
}

export const AddOrSetMutateObjectSchema = msObject({
    resource: z.unknown(),
    parentId: IdSchema,
    withoutGrowl: z.boolean().optional(),
});

export interface AddOrSetMutateObject<R extends PersistableResource> {
    resource: R;
    parentId: string | undefined;
    withoutGrowl?: boolean;
}

export const NewRootVersionMutateObjectSchema = msObject({
    resource: z.unknown(),
    parentId: IdSchema,
});

export interface NewRootVersionMutateObject<R extends PersistableResource> {
    parentId: string | undefined;
    resource?: R;
}

export const AddOrSetMutateObjectWithParentRelationSchema = msObject({
    resource: z.unknown(),
    parentId: IdSchema,
    parentRelation: z.string().optional(),
});

export interface AddOrSetMutateObjectWithParentRelation<R extends ArtifactResource> {
    resource: R;
    parentId: string | undefined;
    parentRelation: string | undefined;
}

export const AddToIndexMutateObjectSchema = msObject({
    resource: z.unknown(),
    index: z.number().nullable(),
    parentId: IdSchema,
});

export interface AddToIndexMutateObject<R extends ArtifactResource> {
    resource: R;
    index: number | null;
    parentId: string;
}

export const AddToIndexMutateObjectWithParentRelationSchema = msObject({
    resource: z.unknown(),
    index: z.number().optional(),
    parentId: IdSchema,
    parentRelation: z.string(),
});

export interface AddToIndexMutateObjectWithParentRelation<R extends ArtifactResource> {
    resource: R;
    index: number | undefined;
    parentId: string;
    parentRelation: string;
}

export const UpdateIndexMutateObjectSchema = msObject({
    resource: z.unknown(),
    index: z.number(),
    parentId: IdSchema,
});

export interface UpdateIndexMutateObject<R extends ArtifactResource> {
    resource: R;
    index: number;
    parentId: string | undefined;
}

export const AddResourceWithVariantMutateObjectSchema = msObject({
    payload: z.unknown(),
    parentId: IdSchema,
});

export interface AddResourceWithVariantMutateObject<R extends ArtifactResource, V extends ArtifactResource> {
    payload: AddResourceWithVariantObject<R, V>;
    parentId: string | undefined;
}

export interface BatchAddWithVariantMutateObject<R extends ArtifactResource, V extends ArtifactResource> {
    payload: Record<string, Array<AddResourceWithVariantObject<R, V>>>;
}

export interface AddResourceWithVariantObject<R extends ArtifactResource, V extends ArtifactResource> {
    resource: R;
    variant: V;
}

export const AddImplementationWithDefinitionMutateObjectSchema = msObject({
    payload: z.unknown(),
    parentId: IdSchema,
});

export interface AddImplementationWithDefinitionMutateObject<R extends ArtifactResource, V extends ArtifactResource> {
    payload: AddImplementationWithDefinitionObject<R, V>;
    parentId: string | undefined;
}

export interface BatchAddImplementationWithDefinitionMutateObject<
    R extends ArtifactResource,
    V extends ArtifactResource,
> {
    payload: Record<string, Array<AddImplementationWithDefinitionObject<R, V>>>;
}

export const AddImplementationWithDefinitionAndParentRelationMutateObjectSchema = msObject({
    payload: z.unknown(),
    parentId: IdSchema,
    parentRelation: z.string().optional(),
});

export interface AddImplementationWithDefinitionAndParentRelationMutateObject<
    R extends ArtifactResource,
    V extends ArtifactResource,
> {
    payload: AddImplementationWithDefinitionObject<R, V>;
    parentRelation: string | undefined;
    parentId: string | undefined;
}

export interface AddImplementationWithDefinitionObject<R extends ArtifactResource, V extends ArtifactResource> {
    implementation: R;
    definition: V;
}

export const BatchAddImplementationWithDefinitionMutateObjectSchema = msObject({
    payload: z.unknown(),
});

export interface BatchAddImplementationWithDefinitionAndParentRelationMutateObject<
    R extends ArtifactResource,
    V extends ArtifactResource,
> {
    payload: Record<string, BatchAddImplementationWithDefinitionAndParentRelationValueObject<R, V>>;
}

export interface BatchAddImplementationWithDefinitionAndParentRelationValueObject<
    R extends ArtifactResource,
    V extends ArtifactResource,
> {
    parentRelation: string | undefined;
    toAdd: Array<AddImplementationWithDefinitionObject<R, V>>;
}

export const AddResourceWithInterfaceMutateObjectSchema = msObject({
    payload: z.unknown(),
    parentId: IdSchema,
    dataDictionaryVersionId: IdSchema,
});

export interface AddResourceWithInterfaceMutateObject<
    R extends ArtifactResource,
    I extends ArtifactResource,
    V extends ArtifactResource,
> {
    payload: AddResourceWithInterfaceObject<R, I, V>;
    parentId: string | undefined;
    dataDictionaryVersionId: string | undefined;
}

export const BatchAddResourceWithInterfaceMutateObjectSchema = msObject({
    payload: z.unknown(),
    parentId: IdSchema,
});

export interface BatchAddResourceWithInterfaceMutateObject<
    R extends ArtifactResource,
    I extends ArtifactResource,
    V extends ArtifactResource,
> {
    payload: Record<string, Array<AddResourceWithInterfaceObject<R, I, V>>>;
    parentId: string | undefined;
}

export interface AddResourceWithInterfaceObject<
    R extends ArtifactResource,
    I extends ArtifactResource,
    V extends ArtifactResource,
> {
    resource: R;
    interfaceResource: I;
    interfaceVariantResource: V;
}

export const NamingConventionReferenceMutateObjectSchema = msObject({
    projectVariantVersionId: IdSchema,
    namingConventionVersionId: IdSchema.nullable(),
});

export type NamingConventionReferenceMutateObject = z.infer<typeof NamingConventionReferenceMutateObjectSchema>;
